import { DataRoomTeaserFile } from './DataRoomTeaserFile';
import { DataRoomTeaserPreview } from './DataRoomTeaserPreview';
import { DealPartnerContact } from './DealPartnerContact';
import { Tier1BankTypes } from './TrancheEvaluation';

export interface DataRoom {
	guid: string;
	dealGuid: string;
	rootDataRoomFolderGuid: string;
	roomStatus: DataRoomStatus;
	useDataRoomReminders: boolean;
	outsideDataRoomUrl: string;
	dataRoomWeeklyEmail: DataRoomWeeklyEmail;
	hubSpotId: string;
	createdOn: string;
	modifiedOn: string;
	roomSummaryText: string;
	teaserFiles: DataRoomTeaserFile[];
	totalFileSize: number;
	totalMegabytes: number;
	dealPartnerContact: DealPartnerContact | null;
	hasAcceptedTermSheet: boolean;
	customAgreementText: string;
	showFeedbackSummary: boolean;
	isEditingFeedbackSummary: boolean;
	showActiveSummaryReport: boolean;
	isRawLead: boolean;
	isRawLeadVisible: boolean;
	isDirectIntroDeal: boolean;
}

export interface ActiveDataRoom {
	guid: string;
	dealName: string;
}

export type DataRoomStatus = keyof typeof DataRoomStatusMap;
export const DataRoomStatusMap = {
	Closed: 'Closed',
	Open: 'Open',
};

export type DataRoomWeeklyEmail = keyof typeof DataRoomWeeklyEmailMap;
export const DataRoomWeeklyEmailMap = {
	Both: 'Send to Both Borrower and Lenders',
	BorrowersOnly: 'Send to Borrower Contacts Only',
	LendersOnly: 'Send to Lenders Only',
	NoSend: 'Do not send',
};

export interface UpdateDataRoomDto
	extends Omit<Partial<DataRoom>, 'teaserFiles' | 'modifiedOn' | 'createdOn' | 'guid' | 'dealGuid'> {
	roomStatus?: DataRoomStatus;
	useDataRoomSequencing?: boolean;
	useDataRoomReminders?: boolean;
	outsideDataRoomUrl?: string;
	dataRoomWeeklyEmail?: DataRoomWeeklyEmail;
	hubSpotId?: string;
	roomSummaryText?: string;
	customAgreementText?: string;
	page: DataRoomPage;
}

export type DataRoomPage = 'Summary' | 'Settings';

export interface DataRoomSummary {
	id: number;
	dataRoomId: number;
	dealId: number;
	dataRoomGuid: string;
	dealGuid: string;
	dealName: string;
	totalRequestedAmount: number;
	isAsapTargetDate: boolean;
	targetFundingDate: string;
	teasersSent: number;
	teasersAccepted: number;
	teasersDeclined: number;
	teasersPending: number;
	teasersDeleted: number;
	totalTermSheets: number;
	totalDocuments: number;
	lastDocumentUploaded: string;
	lendersAccessed: number;
	lendersInvited: number;
	lendersAccepted: number;
	lendersDeclined: number;
	lendersPending: number;
	lendersActive: number;
	dataRoomStatus: DataRoomStatus;
	totalDiligenceRequests: number;
	diligenceRequestsOpen: number;
	diligenceRequestsClosed: number;
	totalLoanApplicationTasks: number;
	loanApplicationTasksOpen: number;
	loanApplicationTasksClosed: number;
	launchedDate: string;
	institutionTeasersSent: number;
	institutionTeasersAccepted: number;
	institutionTeasersDeclined: number;
	institutionTeasersPending: number;
	institutionTeasersDeleted: number;
	institutionLendersAccessed: number;
	institutionLendersInvited: number;
	institutionLendersAccepted: number;
	institutionLendersDeclined: number;
	institutionLendersPending: number;
	institutionLendersActive: number;
}

export interface DirectConnectLenderInvite {
	id: number;
	guid: string;
	bankName: string;
	tier1BankType: Tier1BankTypes;
	lenderName: string;
	createdOn: string;
	isInterested: boolean | null;
	inviteStatus: RawLeadInterestLenderStatus;
	borrowerStatus: BorrowerRawLeadInterest;
}

export type BorrowerRawLeadInterest = keyof typeof BorrowerRawLeadInterestMap;
export const BorrowerRawLeadInterestMap = {
	PendingResponse: 'Pending',
	Interested: 'Interested',
	NotInterested: 'Not Interested',
	PendingLender: 'Pending Lender Response',
};

export interface LenderInviteView {
	dataRoom: DataRoom;
	invites: DirectConnectLenderInvite[];
	teaserPreview: DataRoomTeaserPreview;
}

export type RawLeadInterestLenderStatus = keyof typeof RawLeadInterestLenderStatusMap;
export const RawLeadInterestLenderStatusMap = {
	PendingResponse: 'Pending',
	Interested: 'Accepted',
	NotInterested: 'Declined',
};
